/**
 * PR-I — Layer visibility / lock / freeze state for the scene3d renderer.
 *
 * # AutoCAD parity (CLAUDE.md ПРИОРИТЕТ #1)
 *
 * AutoCAD layers carry three independent boolean state bits the renderer
 * must respect:
 *
 *   - **Off / On (visible)**     — entity is hidden, but still loaded and
 *                                  selectable via OBJID. Renderer skips
 *                                  the bucket entirely.
 *   - **Frozen**                  — entity is hidden AND skipped by every
 *                                  query (regen, plot, hit-test). Treated
 *                                  identically to Off in the renderer
 *                                  (both flip `mesh.visible = false` and
 *                                  remove the layer from hit-test maps).
 *                                  The distinction is preserved at the
 *                                  command-engine layer; the renderer only
 *                                  cares about the combined effect.
 *   - **Locked**                  — entity is rendered but cannot be
 *                                  picked / selected / edited. We surface
 *                                  this via reduced opacity (`0.5`) +
 *                                  `depthWrite=false` so locked geometry
 *                                  stays visible but visually fades, and
 *                                  hit-test pre-filters candidates whose
 *                                  layer is in the locked set.
 *
 * Exact opacity (0.5) matches the SVG renderer's locked-layer pass and the
 * AutoCAD LAYLOCKFADECTL = 50% default.
 *
 * # Why pure module
 *
 * `applyLayerVisibility` walks the BatchedLayer buckets + their materials
 * and mutates `.visible` / `.opacity` / `.transparent` / `.depthWrite` in
 * place. It deliberately does NOT touch React state, the spatial index,
 * or selection — the caller (Scene3DStage's effect) owns the
 * cross-system glue. Keeping the helper pure makes unit tests trivial:
 * no THREE / GL context required.
 *
 * # Re-bucket on color/linetype change
 *
 * `selectAffectedEntitiesByLayer` returns the set of entity ids whose
 * layer matches a layer that mutated colour / linetype. The caller passes
 * this set to `BatchedLayer.updateEntity(id, renderable)` after re-running
 * its adapter so the bucket key (`layer × color × linetype`) flips. We do
 * NOT re-bucket from inside this module — the entity → renderable adapter
 * pipeline lives in Scene3DStage.
 */

import type { BatchedLayerApi } from "../batchedLayer";
import { isHiddenByLayerChain } from "../layerChainVisibility";
import type { RenderableEntity } from "../types";

/**
 * Minimal layer-state shape the visibility layer consumes. The
 * `LayerDefinition` from `@dxf-ai-editor/drawing-model` is a strict
 * superset; the test suite uses this narrower shape so we don't pull
 * the drawing-model package into the renderer module.
 */
export interface LayerVisibilityState {
  name: string;
  /** AutoCAD On/Off — the user-visible "show this layer" toggle. */
  visible: boolean;
  /** AutoCAD Frozen — same effect as `!visible` from renderer's POV. */
  frozen: boolean;
  /** AutoCAD Lock — render at reduced opacity + skip hit-test. */
  locked: boolean;
}

/**
 * Mesh + material handle pair for a bucket. The renderer registers each
 * bucket's `LineSegments2` here; the visibility helper mutates `.visible`
 * + material opacity in place.
 *
 * `MaterialLike` covers both `LineMaterial` (line buckets) and
 * `MeshBasicMaterial` (hatch / wipeout) without importing THREE — the
 * helper only needs the mutable opacity / transparent / depthWrite
 * fields. Tests stub these with plain objects.
 */
export interface MeshLike {
  visible: boolean;
}
export interface MaterialLike {
  opacity: number;
  transparent: boolean;
  depthWrite: boolean;
  /** Some materials need a uniforms re-flush to honour opacity diff. */
  needsUpdate?: boolean;
}

/**
 * One bucket's render handles, keyed inside the renderer by the bucket's
 * `keyId`. The helper iterates a Map<bucketKeyId, BucketRenderHandle> and
 * applies visibility / opacity per (layer, locked) bit.
 */
export interface BucketRenderHandle {
  /** Layer name this bucket belongs to (read from the bucket's MeshKey). */
  layer: string;
  /**
   * Codex PR #784 round 3 P1 — the enclosing block-reference layers of the
   * bucket's entities (`MeshKey.parentLayers`); the bucket hides / fades
   * when ANY of them is hidden / locked, in addition to `layer`
   * (`isHiddenByLayerChain`). Absent for a plain top-level bucket.
   */
  parentLayers?: readonly string[];
  mesh: MeshLike;
  material: MaterialLike;
  /**
   * Original opacity as set by the bucket builder (1.0 for solid,
   * possibly <1 for hatches with alpha pattern fills). Restored when
   * the layer is unlocked.
   */
  baseOpacity: number;
  /**
   * Original `transparent` flag — restored when unlocked. Hatch fill
   * materials may already be transparent; we don't want to flip them
   * back to opaque on unlock.
   */
  baseTransparent: boolean;
  /**
   * Original `depthWrite` flag — restored when unlocked. Hatches /
   * wipeouts use depthWrite=false; we preserve that.
   */
  baseDepthWrite: boolean;
}

/** Constant used by AutoCAD's LAYLOCKFADECTL (50% fade). */
export const LOCKED_LAYER_OPACITY = 0.5;

/**
 * Apply per-layer visibility / lock state to a registry of bucket render
 * handles. Returns a fresh `Set<string>` of locked layer names so the
 * caller can pre-filter hit-test candidates.
 *
 * Mutation contract (per handle, through `applyLayerVisibilityToHandle`):
 *   - mesh.visible = the handle's layer AND every `parentLayers` entry is
 *     visible && !frozen (Codex PR #784 round 3 P1 — a block child hides
 *     with its enclosing INSERT's layer as well as its own)
 *   - if any of them is locked: material.opacity = LOCKED_LAYER_OPACITY,
 *     material.transparent = true, material.depthWrite = false
 *   - else: material.opacity = baseOpacity (≤ 1),
 *     material.transparent = baseTransparent,
 *     material.depthWrite = baseDepthWrite
 *
 * Buckets whose layer is missing from `layerStates` are treated as
 * "fully visible, unlocked" — matches AutoCAD's behaviour for entities
 * on layers that haven't been loaded yet.
 *
 * AutoCAD layer names are case-sensitive in DXF (group code 2), but most
 * CAD tools lower-case for comparison — the original case is kept here
 * because BatchedLayer stores the layer name verbatim from the entity.
 */
export function applyLayerVisibility(
  buckets: ReadonlyMap<string, BucketRenderHandle>,
  layerStates: ReadonlyArray<LayerVisibilityState>,
): { lockedLayers: Set<string>; hiddenLayers: Set<string> } {
  const lockedLayers = new Set<string>();
  const hiddenLayers = new Set<string>();
  for (const state of layerStates) {
    if (!state.visible || state.frozen) hiddenLayers.add(state.name);
    if (state.locked) lockedLayers.add(state.name);
  }

  for (const handle of buckets.values()) {
    applyLayerVisibilityToHandle(handle, hiddenLayers, lockedLayers);
  }

  return { lockedLayers, hiddenLayers };
}

/**
 * Apply hidden/locked state to ONE freshly-built bucket handle from the
 * derived `hiddenLayers` / `lockedLayers` sets (NOT the full
 * `LayerVisibilityState[]` table).
 *
 * # Why this exists (FIX-2 — Codex P2, FrameLoops relift)
 *
 * The viewport-cull relift path in `FrameLoops.tsx` materialises a NEW
 * bucket AFTER `applyAndMirrorLayerVisibility` has already run for the
 * pass. Without re-applying the current layer state, that bucket renders
 * VISIBLE even when its layer is hidden / frozen (and at full opacity
 * even when locked) until the layer table next mutates or a full
 * bulk-load runs. The relift path keeps only the two derived sets
 * (`hiddenLayersRef` / `lockedLayersRef`) — not the source state array —
 * so this helper applies the SAME mutation contract as
 * `applyLayerVisibility` using those sets.
 *
 * Mutation contract (the ONE body `applyLayerVisibility` applies per handle):
 *   - mesh.visible = neither `handle.layer` nor any `handle.parentLayers`
 *                    entry is in `hiddenLayers` (`isHiddenByLayerChain`)
 *   - locked  (any of them) → opacity = LOCKED_LAYER_OPACITY,
 *               transparent = true, depthWrite = false
 *   - unlocked → restore baseOpacity / baseTransparent / baseDepthWrite
 */
export function applyLayerVisibilityToHandle(
  handle: BucketRenderHandle,
  hiddenLayers: ReadonlySet<string>,
  lockedLayers: ReadonlySet<string>,
): void {
  handle.mesh.visible = !isHiddenByLayerChain(
    handle.layer,
    handle.parentLayers,
    hiddenLayers,
  );
  if (isHiddenByLayerChain(handle.layer, handle.parentLayers, lockedLayers)) {
    handle.material.opacity = LOCKED_LAYER_OPACITY;
    handle.material.transparent = true;
    handle.material.depthWrite = false;
  } else {
    handle.material.opacity = handle.baseOpacity;
    handle.material.transparent = handle.baseTransparent;
    handle.material.depthWrite = handle.baseDepthWrite;
  }
  if ("needsUpdate" in handle.material) {
    handle.material.needsUpdate = true;
  }
}

/**
 * Re-bucket trigger: identify entities whose layer changed colour or
 * linetype, so the caller can call `BatchedLayer.updateEntity(id, ...)`
 * with a freshly-resolved renderable. This is a pure inspector — it
 * does NOT touch the BatchedLayer or any mesh.
 *
 * The return shape is a Map<entityId, layerName> rather than a plain
 * Set so the caller can preserve the layer-name → entity grouping when
 * scheduling the re-bucket.
 */
export function selectAffectedEntitiesByLayer(
  entities: ReadonlyArray<RenderableEntity>,
  changedLayers: ReadonlySet<string>,
): Map<string, string> {
  const out = new Map<string, string>();
  if (changedLayers.size === 0) return out;
  for (const entity of entities) {
    if (changedLayers.has(entity.layer)) {
      out.set(entity.id, entity.layer);
    }
  }
  return out;
}

/**
 * Entity-level filter for hit-test. Pre-filters renderables whose layer
 * is in the locked or hidden set; the renderer's `pick` / `selectInWindow`
 * call this on the spatial-index intersect output.
 *
 * Locked: filtered. Hit-test treats locked entities as non-pickable
 *         (matches AutoCAD — locked entities are visually visible but
 *         cannot be selected for editing).
 * Hidden: filtered. Frozen / Off layers are not in the spatial index in
 *         normal flow, but we double-check here for the case where the
 *         renderer keeps stale entries between feed updates.
 *
 * Both checks run over the candidate's block-reference layer chain too
 * (`renderParentLayers`, Codex PR #784 round 3 P1): on the legacy branch a
 * block child is its own index entry, and AutoCAD makes it unpickable when
 * its enclosing INSERT's layer is hidden or locked.
 */
export function filterHittableEntities<
  E extends { layer: string; renderParentLayers?: readonly string[] },
>(
  candidates: ReadonlyArray<E>,
  lockedLayers: ReadonlySet<string>,
  hiddenLayers: ReadonlySet<string>,
): E[] {
  if (lockedLayers.size === 0 && hiddenLayers.size === 0) {
    // Fast path: no filtering needed; return original (caller can
    // sort in place since we still allocate when filtering).
    return candidates.slice();
  }
  const out: E[] = [];
  for (const c of candidates) {
    if (isHiddenByLayerChain(c.layer, c.renderParentLayers, lockedLayers)) continue;
    if (isHiddenByLayerChain(c.layer, c.renderParentLayers, hiddenLayers)) continue;
    out.push(c);
  }
  return out;
}

/**
 * Build a fresh `BucketRenderHandle` registry from a BatchedLayer's
 * buckets + a mesh/material lookup. The caller (Scene3DStage's effect)
 * walks `batched.getBuckets()` once after `bulkLoad`, captures the
 * baseOpacity / baseTransparent / baseDepthWrite, and feeds the result
 * to `applyLayerVisibility`.
 *
 * Bucket layer name comes from the bucket's `MeshKey.layer` — that's
 * what BatchedLayer derives from entity.layer when packing.
 */
export function buildBucketRenderHandles<M extends MeshLike, T extends MaterialLike>(
  batched: BatchedLayerApi,
  meshLookup: ReadonlyMap<string, M>,
  materialLookup: ReadonlyMap<string, T>,
): Map<string, BucketRenderHandle> {
  const out = new Map<string, BucketRenderHandle>();
  for (const bucket of batched.getBuckets()) {
    const mesh = meshLookup.get(bucket.keyId);
    const material = materialLookup.get(bucket.keyId);
    if (!mesh || !material) continue;
    out.set(bucket.keyId, {
      layer: bucket.key.layer,
      ...(bucket.key.parentLayers ? { parentLayers: bucket.key.parentLayers } : {}),
      mesh,
      material,
      baseOpacity: material.opacity,
      baseTransparent: material.transparent,
      baseDepthWrite: material.depthWrite,
    });
  }
  return out;
}
